// Feedback #505 — searchable client picker.  Replaces the plain <select> used
// on Matters new/edit, Communications, Trust deposit/disbursement.  Type-ahead
// filter + alphabetical sort by display name (companies AND individuals sort
// together, not clumped).  Uses headlessui's Combobox for keyboard nav +
// accessibility (aria-expanded, aria-selected, arrow keys, Home/End, Esc).
//
// Props:
//   clients      — array of client objects (first_name, last_name, company_name, id, client_type)
//   value        — currently selected client id (number or string; empty for none)
//   onChange     — (newId: number | '') => void
//   id           — html id for the input (also used for htmlFor label association)
//   required     — when true, renders a hidden <input required> so native form
//                  validation still fires (headlessui's input isn't a form field)
//   placeholder  — override default placeholder
//   disabled     — disable the picker
//   filter       — optional function (client) => boolean to narrow the list
//                  (e.g. active-only)
//   className    — override outer wrapper class

import { useMemo, useState } from 'react'
import { Combobox, ComboboxInput, ComboboxOptions, ComboboxOption, ComboboxButton } from '@headlessui/react'

// Display name: for entity clients or clients missing last_name, use
// company_name; for individuals, use "Last, First".  Ensures alphabetical
// sort places "Acme Corp" between "A. Smith" and "Ace Widgets" instead of
// clumping companies at the top under empty last_name.
export function clientDisplayName (c) {
  if (!c) return ''
  const isEntity = c.client_type === 'entity' || (!c.last_name && c.company_name)
  if (isEntity && c.company_name) return c.company_name
  const last = (c.last_name || '').trim()
  const first = (c.first_name || '').trim()
  if (last && first) return `${last}, ${first}`
  if (last) return last
  if (first) return first
  return c.company_name || '(unnamed client)'
}

export default function ClientPicker ({
  clients = [],
  value,
  onChange,
  id,
  required = false,
  placeholder = 'Start typing a client name…',
  disabled = false,
  filter,
  className = '',
}) {
  const [query, setQuery] = useState('')

  // Sort + filter the client list once per (clients, filter) change.
  const sorted = useMemo(() => {
    const arr = filter ? clients.filter(filter) : [...clients]
    arr.sort((a, b) => clientDisplayName(a).toLowerCase().localeCompare(clientDisplayName(b).toLowerCase()))
    return arr
  }, [clients, filter])

  // Filter down by query — case-insensitive match across display name +
  // first name + company + email so all common recall paths work.
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return sorted
    return sorted.filter(c => {
      const name = clientDisplayName(c).toLowerCase()
      const first = (c.first_name || '').toLowerCase()
      const email = (c.email || '').toLowerCase()
      const company = (c.company_name || '').toLowerCase()
      return name.includes(q) || first.includes(q) || email.includes(q) || company.includes(q)
    })
  }, [sorted, query])

  // Coerce value to number for equality (form state often carries strings).
  const numericValue = value === '' || value == null ? null : Number(value)
  const selected = sorted.find(c => c.id === numericValue) || null

  return (
    <div className={`relative ${className}`}>
      <Combobox
        value={selected}
        onChange={(c) => {
          onChange?.(c ? c.id : '')
          setQuery('')
        }}
        disabled={disabled}
        immediate
      >
        <div className="relative">
          <ComboboxInput
            id={id}
            className="w-full border border-gray-300 rounded-lg pl-3 pr-10 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100 disabled:text-gray-500"
            displayValue={(c) => clientDisplayName(c)}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={placeholder}
            autoComplete="off"
          />
          <ComboboxButton className="absolute inset-y-0 right-0 flex items-center pr-2 text-gray-400 hover:text-gray-600" title="Show list">
            <span aria-hidden="true">▾</span>
          </ComboboxButton>
        </div>
        <ComboboxOptions
          anchor="bottom start"
          className="w-[var(--input-width)] mt-1 max-h-72 overflow-auto rounded-lg border border-gray-200 bg-white shadow-lg z-50 text-sm empty:hidden"
        >
          {filtered.length === 0 && query.trim() && (
            <div className="px-3 py-2 text-gray-500 text-xs italic">
              No clients match &quot;{query}&quot;.
            </div>
          )}
          {filtered.map((c) => (
            <ComboboxOption
              key={c.id}
              value={c}
              className="px-3 py-2 cursor-pointer data-[focus]:bg-primary-50 data-[selected]:font-semibold flex items-center justify-between gap-2"
            >
              <span className="truncate">{clientDisplayName(c)}</span>
              {c.client_type === 'entity' && (
                <span className="text-[10px] uppercase tracking-wide text-gray-400">Entity</span>
              )}
            </ComboboxOption>
          ))}
        </ComboboxOptions>
      </Combobox>
      {/* Hidden field to preserve HTML5 `required` semantics — headlessui's
          combobox input doesn't participate in native form validation. */}
      {required && (
        <input
          tabIndex={-1}
          aria-hidden="true"
          required
          value={numericValue == null ? '' : String(numericValue)}
          onChange={() => {}}
          onFocus={(e) => e.currentTarget.blur()}
          className="sr-only"
          style={{ position: 'absolute', height: 1, width: 1, opacity: 0, pointerEvents: 'none' }}
        />
      )}
    </div>
  )
}
